/* eslint-disable implicit-arrow-linebreak */
import React, { ComponentType, useMemo } from 'react';
import {
  NumericFormatProps,
  NumberFormatValues,
  NumericFormat,
  InputAttributes,
  numericFormatter,
} from 'react-number-format';

import { Input } from '@ast/magma/components/input';

import { SourceInfo } from 'react-number-format/types/types';

import { Sign } from '@app/common/components/AccountTotalUnrealizedPlPercent/AccountTotalUnrealizedPlPercent';

const POSITIVE_SIGN_SYMBOL = '+';
const MAX_VALUE: number = 9999999.99;

export type CurrencyNumberFormatProps = NumericFormatProps & {
  readonly isStylized?: boolean;
  readonly isPositiveSignShown?: boolean;
  readonly maxAllowedLimit?: number;
  readonly label?: string | null;
};

export const CurrencyNumberFormat = React.forwardRef(
  (props: CurrencyNumberFormatProps, ref: React.Ref<NumericFormatProps>) => {
    const {
      onChange,
      onValueChange,
      isStylized,
      isPositiveSignShown,
      maxAllowedLimit,
      ...numberFormatProps
    } = props;

    const wrapValue = (formattedValue: string, template = '<span>$1</span>') => ({
      __html: isStylized ? formattedValue.replace(/(\.(.*)+)/g, template) : formattedValue,
    });

    const renderPositiveSign = useMemo(() => (
      isPositiveSignShown
        && typeof numberFormatProps.value !== 'undefined' && numberFormatProps.value !== null
        && Math.sign(parseFloat(numberFormatProps.value.toString())) === Sign.POSITIVE
        && <span>{POSITIVE_SIGN_SYMBOL}</span>
    ), [isPositiveSignShown, numberFormatProps.value]);

    const isAllowed = (_: NumberFormatValues): boolean =>
      _.floatValue === undefined || _.floatValue <= maxAllowedLimit!;

    return (
      <NumericFormat
        data-stable-name="CurrencyNumberInput"
        customInput={Input as ComponentType<InputAttributes>}
        thousandSeparator
        decimalScale={2}
        inputMode="decimal"
        fixedDecimalScale
        renderText={(formattedValue) => (
          <>
            { renderPositiveSign }
            {/* eslint-disable-next-line react/no-danger */}
            <span dangerouslySetInnerHTML={wrapValue(formattedValue)} data-stable-name="CurrencyNumberSpan" />
          </>
        )}
        onValueChange={(values: any, sourceInfo: SourceInfo) => {
          // Do not return undefined, causes problems with defaultValue of react-hook-form
          if (onChange) {
            onChange(values.floatValue !== undefined ? values.floatValue : null);
          }

          if (onValueChange) {
            onValueChange(values.floatValue !== undefined ? values.floatValue : null, sourceInfo);
          }
        }}
        prefix="$"
        isAllowed={isAllowed}
        getInputRef={ref}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...numberFormatProps}
      />
    );
  },
);

CurrencyNumberFormat.defaultProps = {
  isStylized: false,
  isPositiveSignShown: false,
  maxAllowedLimit: MAX_VALUE,
};

// TODO: remove it workaround
export const getFormattedCurrencyNumber = (
  value: number | null = 0,
  decimalScale: number = 2,
  prefix: string = '$',
) => numericFormatter(String(value ?? ''), {
  decimalScale,
  prefix,
  fixedDecimalScale: true,
  displayType: 'input',
  thousandSeparator: true,
  allowLeadingZeros: false,
  allowNegative: true,
  type: 'text',
  decimalSeparator: '.',
});

export default CurrencyNumberFormat;
